function thrScrollRoot() {
  if (window.matchMedia('(min-width: 990px)').matches) {
    return document.querySelector('.page-wrapper') || window;
  }
  return window;
}

function thrSplitWords(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current);
    current = walker.nextNode();
  }

  const words = [];
  textNodes.forEach((textNode) => {
    const text = textNode.nodeValue;
    if (!text || !/\S/.test(text)) return;

    const fragment = document.createDocumentFragment();
    text.split(/(\s+)/).forEach((part) => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        fragment.appendChild(document.createTextNode(part));
        return;
      }
      const word = document.createElement('span');
      word.className = 'thr-scroll-copy__word';
      word.textContent = part;
      fragment.appendChild(word);
      words.push(word);
    });
    textNode.parentNode.replaceChild(fragment, textNode);
  });

  return words;
}

/**
 * GSAP-style word stagger. About five words are mid-fade at once.
 * progress 0 keeps every word faint. progress 1 leaves every word solid.
 */
function thrWordOpacity(index, count, progress, startOpacity) {
  const stagger = 0.1;
  const duration = 0.5;
  const total = duration + stagger * Math.max(count - 1, 0);
  const local = (progress * total - index * stagger) / duration;
  const amount = local <= 0 ? 0 : local >= 1 ? 1 : local;
  return Math.round((startOpacity + (1 - startOpacity) * amount) * 100) / 100;
}

class ThrScrollCopy extends HTMLElement {
  connectedCallback() {
    this.base = this.querySelector('.thr-scroll-copy__base');
    this.reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopQuery = window.matchMedia('(min-width: 990px)');
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
    this.onContextChange = this.onContextChange.bind(this);
    this.ticking = false;
    this.listening = false;
    this.words = null;
    this.lastProgress = -1;
    this.startOpacity = 0.1;

    this.reduceQuery.addEventListener('change', this.onContextChange);
    this.desktopQuery.addEventListener('change', this.onContextChange);
    this.activate();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected) this.update();
      });
    }
  }

  disconnectedCallback() {
    this.deactivate();
    this.reduceQuery.removeEventListener('change', this.onContextChange);
    this.desktopQuery.removeEventListener('change', this.onContextChange);
  }

  onContextChange() {
    this.deactivate();
    this.lastProgress = -1;
    if (this.reduceQuery.matches) {
      this.clearWordOpacity();
      return;
    }
    this.activate();
  }

  activate() {
    if (!this.base || this.reduceQuery.matches) return;

    const raw = getComputedStyle(this).getPropertyValue('--thr-scroll-copy-start');
    const start = Number(raw);
    if (Number.isFinite(start)) this.startOpacity = Math.min(1, Math.max(0, start));

    if (!this.words) this.words = thrSplitWords(this.base);
    if (!this.words.length) return;

    this.scroller = thrScrollRoot();
    const root = this.scroller === window ? null : this.scroller;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.listen();
          this.update();
        } else {
          this.unlisten();
        }
      },
      { root: root, rootMargin: '20% 0px' }
    );

    this.observer.observe(this);
    this.update();
  }

  deactivate() {
    this.unlisten();
    if (this.observer) this.observer.disconnect();
    this.observer = null;
  }

  clearWordOpacity() {
    if (!this.words) return;
    this.words.forEach((word) => {
      word.style.opacity = '';
      word._thrOpacity = undefined;
    });
  }

  listen() {
    if (this.listening || !this.scroller) return;
    this.listening = true;
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onScroll, { passive: true });
      window.visualViewport.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  unlisten() {
    if (!this.listening) return;
    this.listening = false;
    this.scroller.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onScroll);
      window.visualViewport.removeEventListener('scroll', this.onScroll);
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.ticking = false;
    if (!this.base || !this.words || !this.words.length || !this.isConnected || this.reduceQuery.matches) return;

    const viewHeight =
      this.scroller && this.scroller !== window ? this.scroller.clientHeight : window.innerHeight;
    let solid = Number(this.dataset.solid);
    let fade = Number(this.dataset.fade);
    if (!Number.isFinite(solid)) solid = 16;
    if (!Number.isFinite(fade)) fade = 84;
    if (fade < solid + 8) fade = Math.min(solid + 8, 100);

    const rect = this.base.getBoundingClientRect();
    let top = rect.top;
    if (this.scroller && this.scroller !== window) {
      top -= this.scroller.getBoundingClientRect().top;
    }

    const fadeY = (viewHeight * fade) / 100;
    const solidY = (viewHeight * solid) / 100;
    const travel = fadeY - solidY + rect.height;
    const rawProgress = travel <= 0 ? 1 : (fadeY - top) / travel;
    const progress = rawProgress <= 0 ? 0 : rawProgress >= 1 ? 1 : rawProgress;
    const key = Math.round(progress * 1000);
    if (key === this.lastProgress) return;
    this.lastProgress = key;

    const count = this.words.length;
    for (let index = 0; index < count; index += 1) {
      const opacity = thrWordOpacity(index, count, progress, this.startOpacity);
      const word = this.words[index];
      if (word._thrOpacity === opacity) continue;
      word._thrOpacity = opacity;
      word.style.opacity = String(opacity);
    }
  }
}

if (!customElements.get('thr-scroll-copy')) {
  customElements.define('thr-scroll-copy', ThrScrollCopy);
}
